//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

/**
 * MCPDataService
 *
 * Service for managing MCP (Model Context Protocol) connections.
 * Handles Admin CRUD + toggle, User connect/disconnect/toggle,
 * mapping to the actual backend contracts defined in mcp_connectors/routes.py.
 *
 * Admin endpoints:  /admin/mcp-servers/*
 * User endpoints:   /teamspace/<ts_id>/mcp-connectors/*
 */

import axios from './axios';
import Q from 'q';
import async from 'async';
import { User } from 'oidc-client-ts';
import { ResponseType } from 'axios';
import { getSafeToken } from './securityUtils';

type JSONPrimitive = string | number | boolean;
type JSONObject = { [key: string]: JSONValue };
type JSONArray = JSONValue[];
type JSONValue = JSONPrimitive | JSONObject | JSONArray | undefined;

interface QueueOptions {
  method: string;
  url: string;
  body: object;
  headers: { [key: string]: string };
  queryParameters: { [key: string]: string };
  form: FormData;
  deferred: Q.Deferred<any>;
  responseType: ResponseType;
}

function serializeQueryParams(parameters: { [key: string]: string }) {
  const str = [];
  for (const p in parameters) {
    if (parameters.hasOwnProperty(p)) {
      str.push(encodeURIComponent(p) + '=' + encodeURIComponent(parameters[p]));
    }
  }
  return str.join('&');
}

// The 'loop' OOTB server backs Loop Genie itself — disabling it would break
// the AI assistant, so its toggle stays locked wherever OOTB servers are
// shown (MCPTab.tsx's admin list, MCPConnectionsTab.tsx's per-loop list),
// rather than hiding the row entirely. Shared here so both stay in sync.
export const LOCKED_OOTB_SERVER_NAMES = new Set(['loop']);

// ── Admin types (match MCPServerSummary / MCPServerDetail from backend) ──

export interface MCPServerSummary {
  id: string;
  name: string;
  domain: string;
  credential_type: string;
  is_enabled: boolean;
  url?: string;
}

export interface MCPServerDetail {
  id: string;
  name: string;
  domain: string;
  url: string;
  credential_type: string;
  oauth_client_id?: string | null;
  oauth_auth_url?: string | null;
  oauth_token_url?: string | null;
  created_by: string;
  is_enabled: boolean;
}

export interface MCPServerCreateResponse {
  created: MCPServerDetail;
  auto_disabled: MCPServerSummary[];
}

export interface MCPServerUpdateResponse {
  updated: MCPServerDetail;
  auto_disabled: MCPServerSummary[];
  credentials_wiped: boolean;
}

export interface MCPServerToggleResponse {
  toggled: MCPServerSummary;
  auto_disabled: MCPServerSummary[];
}

export interface MCPOAuthStatusResponse {
  server_id: string;
  oauth_required: boolean;
  has_stored_credential: boolean;
}

export interface MCPServerDeleteResponse {
  deleted: MCPServerSummary;
}

export interface MCPValidateResponse {
  server_id: string;
  name: string;
  status: string;
  latency_ms?: number | null;
  error?: string | null;
}

export interface MCPCreatePayload {
  name: string;
  domain: string;
  url: string;
  credential_type: string;
  teamspace_id?: string;
  oauth_client_id?: string;
  oauth_client_secret?: string;
  oauth_auth_url?: string;
  oauth_token_url?: string;
}

export interface MCPCatalogIngestResponse {
  server_id: string;
  teamspace_id: string;
  status: string;
  job_id?: string;
  message?: string;
}

export interface MCPCatalogCleanupResponse {
  server_id: string;
  teamspace_id: string;
  opensearch_cleanup?: { attempted: boolean; deleted_documents: number };
  snapshot_cleanup?: { attempted: boolean };
}

export interface IngestJob {
  job_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  phase: string;
  progress: { current: number; total: number } | null;
  teamspace_id: string;
  created_by: string;
  target_key: string;
  created_at: number;
  updated_at: number;
  server_id?: string | null;
  server_key?: string;
  server_name?: string;
  server_url?: string | null;
  runtime_domain?: string;
  scope?: string;
  error?: string;
}

export interface MCPCatalogJobsResponse {
  teamspace_id: string;
  jobs: IngestJob[];
}

export interface MCPUpdatePayload {
  name?: string;
  domain?: string;
  url?: string;
  credential_type?: string;
  oauth_client_id?: string;
  oauth_client_secret?: string;
  oauth_auth_url?: string;
  oauth_token_url?: string;
}

// ── User types (match UserMCPConnectionItem from backend) ──

export interface UserMCPConnection {
  server_id: string;
  name: string;
  domain: string;
  credential_type: string;
  user_enabled: boolean;
  connected: boolean;
  connected_at?: string | null;
  token_expires_at?: string | null;
}

export interface ConnectResponse {
  server_id: string;
  name: string;
  status: string;
  connected_at?: string | null;
  message?: string | null;
}

export interface OAuthConnectResponse {
  auth_url: string;
}

export interface DisconnectResponse {
  server_id: string;
  name: string;
  disconnected: boolean;
}

export interface UserToggleResponse {
  server_id: string;
  is_enabled: boolean;
  mcp_status?: 'success' | 'failed' | 'skipped';
}

// ── AI Coding Tools types (match ai_coding_tools/routes.py) ──

export interface AICodingToolItem {
  id: string;
  tool_name: string;
  display_name: string;
  auth_type: string;
  api_key: string; // masked value from backend
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface AICodingToolUpsertPayload {
  tool_name: string;
  display_name: string;
  auth_type: string;
  api_key: string;
  enabled: boolean;
}

export interface AICodingToolTogglePayload {
  enabled: boolean;
}

export interface AICodingToolDeleteResponse {
  message: string;
}

class MCPDataService {
  static instance: MCPDataService;
  private readonly queueAPICalls: async.QueueObject<unknown>;
  private readonly analyticsAIUrl: string | undefined;

  constructor(
    getUserContext: () => User,
    getIsAuthenticated: () => boolean,
    analyticsAIUrl: string | undefined,
  ) {
    if (!MCPDataService.instance) {
      MCPDataService.instance = this;
    }

    this.analyticsAIUrl = analyticsAIUrl;
    this.queueAPICalls = async.queue((options: QueueOptions, callback) => {
      MCPDataService.instance._request(
        options.method,
        options.url,
        options.body,
        options.headers,
        options.queryParameters,
        options.form,
        options.deferred,
        function () {
          callback();
        },
        getUserContext,
        getIsAuthenticated,
        analyticsAIUrl,
        options.responseType,
      );
    }, 3);
  }

  public _request(
    method: string,
    url: string,
    body: object,
    headers: { [key: string]: string },
    queryParameters: { [key: string]: string },
    form: FormData | object,
    deferred: Q.Deferred<any>,
    completeJob: () => void,
    getUserContext: () => User,
    getIsAuthenticated: () => boolean,
    baseUrl: string | undefined,
    responseType: ResponseType = 'json',
  ) {
    const user = getUserContext();
    const isAuthenticated = getIsAuthenticated();

    const queryParams =
      queryParameters && Object.keys(queryParameters).length
        ? serializeQueryParams(queryParameters)
        : null;
    const urlWithParams = url + (queryParams ? '?' + queryParams : '');
    headers['Authorization'] = 'Bearer ' + getSafeToken(user.access_token);
    // GET requests carry no body, so Content-Type is dead weight: it adds header
    // bytes to every read and can trigger an unnecessary CORS preflight on
    // cross-origin calls. Strip it centrally so call sites don't have to remember.
    if (method.toUpperCase() === 'GET') {
      delete headers['Content-Type'];
    }
    if (!isAuthenticated) {
      deferred.reject('User is not logged in.');
      completeJob();
      return;
    }
    if (body && !Object.keys(body).length && !Array.isArray(body)) {
      body = {};
    }
    axios({
      method,
      url: urlWithParams,
      baseURL: baseUrl,
      headers,
      responseType: responseType,
      data:
        method === 'POST' || method === 'PUT' || method === 'PATCH'
          ? body
          : undefined,
    })
      .then((response) => {
        deferred.resolve(response.data || response);
        completeJob();
      })
      .catch((error) => {
        deferred.reject(error.response);
        completeJob();
      });
  }

  public request<T>(
    method: string,
    url: string,
    body: object,
    headers: JSONValue,
    queryParameters: JSONValue,
    form: JSONValue,
    deferred: Q.Deferred<T>,
    responseType = 'json',
  ) {
    this.queueAPICalls.push({
      method: method,
      url: url,
      body: body,
      headers: headers,
      queryParameters: queryParameters,
      form: form,
      deferred: deferred,
      responseType: responseType,
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // Admin APIs — /admin/mcp-servers
  // ═══════════════════════════════════════════════════════════════════

  adminListServers(teamspaceId: string): Q.Promise<MCPServerSummary[]> {
    const deferred = Q.defer<MCPServerSummary[]>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'GET',
      '/admin/mcp-servers',
      {},
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  adminGetServer(
    serverId: string,
    teamspaceId: string,
  ): Q.Promise<MCPServerDetail> {
    const deferred = Q.defer<MCPServerDetail>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'GET',
      `/admin/mcp-servers/${serverId}`,
      {},
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  adminCreateServer(
    payload: MCPCreatePayload,
  ): Q.Promise<MCPServerCreateResponse> {
    const deferred = Q.defer<MCPServerCreateResponse>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'POST',
      '/admin/mcp-servers',
      payload,
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise;
  }

  adminUpdateServer(
    serverId: string,
    payload: MCPUpdatePayload,
    teamspaceId: string,
  ): Q.Promise<MCPServerUpdateResponse> {
    const deferred = Q.defer<MCPServerUpdateResponse>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'PUT',
      `/admin/mcp-servers/${serverId}`,
      payload,
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  adminToggleServer(
    serverId: string,
    body: Record<string, string> = {},
  ): Q.Promise<MCPServerToggleResponse> {
    const deferred = Q.defer<MCPServerToggleResponse>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'PATCH',
      `/admin/mcp-servers/${serverId}/toggle`,
      body,
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise;
  }

  adminGetOauthStatus(
    serverId: string,
    teamspaceId: string,
  ): Q.Promise<MCPOAuthStatusResponse> {
    const deferred = Q.defer<MCPOAuthStatusResponse>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'GET',
      `/admin/mcp-servers/${serverId}/oauth-status`,
      {},
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  adminDeleteServer(
    serverId: string,
    teamspaceId: string,
  ): Q.Promise<MCPServerDeleteResponse> {
    const deferred = Q.defer<MCPServerDeleteResponse>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'DELETE',
      `/admin/mcp-servers/${serverId}`,
      {},
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  adminValidateServer(
    serverId: string,
    teamspaceId: string,
  ): Q.Promise<MCPValidateResponse> {
    const deferred = Q.defer<MCPValidateResponse>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'POST',
      `/admin/mcp-servers/${serverId}/validate`,
      {},
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  // ═══════════════════════════════════════════════════════════════════
  // Admin OOTB APIs — /admin/ootb-mcp-servers
  // ═══════════════════════════════════════════════════════════════════

  adminListOotbServers(teamspaceId: string): Q.Promise<MCPServerSummary[]> {
    const deferred = Q.defer<MCPServerSummary[]>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'GET',
      '/admin/ootb-mcp-servers',
      {},
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  adminToggleOotbServer(
    serverId: string,
    teamspaceId?: string,
  ): Q.Promise<MCPServerToggleResponse> {
    const deferred = Q.defer<MCPServerToggleResponse>();
    const headers = { 'Content-Type': 'application/json' };
    const body = teamspaceId ? { teamspace_id: teamspaceId } : {};
    this.request(
      'PATCH',
      `/admin/ootb-mcp-servers/${serverId}/toggle`,
      body,
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise;
  }

  // ═══════════════════════════════════════════════════════════════════
  // User APIs — /teamspace/<ts_id>/mcp-connectors
  // ═══════════════════════════════════════════════════════════════════

  userListConnections(
    teamspaceId: string,
    loopId?: string,
  ): Q.Promise<UserMCPConnection[]> {
    const deferred = Q.defer<UserMCPConnection[]>();
    const headers = { 'Content-Type': 'application/json' };
    const queryParams: { [key: string]: string } = {};
    if (loopId) {
      queryParams['loop_id'] = loopId;
    }
    this.request(
      'GET',
      `/teamspace/${teamspaceId}/mcp-connectors`,
      {},
      headers,
      queryParams,
      {},
      deferred,
    );
    return deferred.promise;
  }

  userToggleConnection(
    teamspaceId: string,
    serverId: string,
    enabled?: boolean,
    loopId?: string,
  ): Q.Promise<UserToggleResponse> {
    const deferred = Q.defer<UserToggleResponse>();
    const headers = { 'Content-Type': 'application/json' };
    const body = typeof enabled === 'boolean' ? { enabled } : {};
    const queryParams: { [key: string]: string } = {};
    if (loopId) {
      queryParams['loop_id'] = loopId;
    }
    this.request(
      'PATCH',
      `/teamspace/${teamspaceId}/mcp-connectors/${serverId}`,
      body,
      headers,
      queryParams,
      {},
      deferred,
    );
    return deferred.promise;
  }

  userConnect(
    teamspaceId: string,
    serverId: string,
    apiKey?: string,
  ): Q.Promise<ConnectResponse | OAuthConnectResponse> {
    const deferred = Q.defer<ConnectResponse | OAuthConnectResponse>();
    const headers = { 'Content-Type': 'application/json' };
    const body: Record<string, string> = {};
    if (apiKey) {
      body.api_key = apiKey;
    }
    this.request(
      'POST',
      `/teamspace/${teamspaceId}/mcp-connectors/${serverId}/connect`,
      body,
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise;
  }

  userDisconnect(
    teamspaceId: string,
    serverId: string,
  ): Q.Promise<DisconnectResponse> {
    const deferred = Q.defer<DisconnectResponse>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'POST',
      `/teamspace/${teamspaceId}/mcp-connectors/${serverId}/disconnect`,
      {},
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise;
  }

  // ═══════════════════════════════════════════════════════════════════
  // Team Space APIs — /team-spaces/<id>/ai-coding-tools
  // ═══════════════════════════════════════════════════════════════════

  listAICodingTools(teamspaceId: string): Q.Promise<AICodingToolItem[]> {
    const deferred = Q.defer<AICodingToolItem[]>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'GET',
      `/team-spaces/${teamspaceId}/ai-coding-tools`,
      {},
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise;
  }

  upsertAICodingTool(
    teamspaceId: string,
    payload: AICodingToolUpsertPayload,
  ): Q.Promise<AICodingToolItem> {
    const deferred = Q.defer<AICodingToolItem>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'POST',
      `/team-spaces/${teamspaceId}/ai-coding-tools`,
      payload,
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise;
  }

  toggleAICodingTool(
    teamspaceId: string,
    toolName: string,
    payload: AICodingToolTogglePayload,
  ): Q.Promise<AICodingToolItem> {
    const deferred = Q.defer<AICodingToolItem>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'PATCH',
      `/team-spaces/${teamspaceId}/ai-coding-tools/${toolName}/toggle`,
      payload,
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise;
  }

  deleteAICodingTool(
    teamspaceId: string,
    toolName: string,
  ): Q.Promise<AICodingToolDeleteResponse> {
    const deferred = Q.defer<AICodingToolDeleteResponse>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'DELETE',
      `/team-spaces/${teamspaceId}/ai-coding-tools/${toolName}`,
      {},
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise;
  }

  // ═══════════════════════════════════════════════════════════════════
  // MCP Catalog APIs — ingest / cleanup
  // ═══════════════════════════════════════════════════════════════════

  catalogIngest(
    teamspaceId: string,
    serverId: string,
  ): Q.Promise<MCPCatalogIngestResponse> {
    const deferred = Q.defer<MCPCatalogIngestResponse>();
    if (!teamspaceId || !serverId) {
      console.error(
        '[MCPDataService] catalogIngest called with missing fields',
        { teamspaceId, serverId },
      );
      deferred.reject({
        data: {
          error: 'Missing required field',
          message:
            "Both 'teamspace_id' and 'server_id' are required for catalog ingest",
        },
      });
      return deferred.promise;
    }
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'POST',
      '/api/mcp/catalog/ingest',
      { teamspace_id: teamspaceId, server_id: serverId },
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise;
  }

  catalogCleanup(
    teamspaceId: string,
    serverId: string,
  ): Q.Promise<MCPCatalogCleanupResponse> {
    const deferred = Q.defer<MCPCatalogCleanupResponse>();
    if (!teamspaceId || !serverId) {
      console.error(
        '[MCPDataService] catalogCleanup called with missing fields',
        { teamspaceId, serverId },
      );
      deferred.reject({
        data: {
          error: 'Missing required field',
          message:
            "Both 'teamspace_id' and 'server_id' are required for catalog cleanup",
        },
      });
      return deferred.promise;
    }
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'POST',
      '/api/mcp/catalog/cleanup',
      { teamspace_id: teamspaceId, server_id: serverId },
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise;
  }

  listIngestJobs(teamspaceId: string): Q.Promise<MCPCatalogJobsResponse> {
    const deferred = Q.defer<MCPCatalogJobsResponse>();
    if (!teamspaceId) {
      deferred.reject({
        data: {
          error: 'Missing required field',
          message: "'teamspace_id' is required to list ingest jobs",
        },
      });
      return deferred.promise;
    }
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'GET',
      '/api/mcp/catalog/jobs',
      {},
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  /**
   * Admin OAuth connect from Integration page.
   * Calls /admin/mcp-servers/<id>/connect-oauth?teamspace_id=<ts>
   * which reuses the existing user OAuth flow with source=integration_page.
   * Returns auth_url to redirect admin to for OAuth consent.
   */
  adminConnectOauth(
    serverId: string,
    teamspaceId: string,
  ): Q.Promise<OAuthConnectResponse> {
    const deferred = Q.defer<OAuthConnectResponse>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'POST',
      `/admin/mcp-servers/${serverId}/connect-oauth`,
      {},
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }
}

export default MCPDataService;
